import React, { useEffect, useState, useRef } from 'react';
import { AiOutlineLogout } from 'react-icons/ai';
import { useParams, useNavigate } from 'react-router-dom';
import { googleLogout } from '@react-oauth/google';
import { FiEdit2 } from 'react-icons/fi';
import { userCreatedPinsQuery, userQuery, userSavedPinsQuery } from '../utils/data';
import { client } from '../client';
import MasonryLayout from './MasonryLayout';
import Spinner from './Spinner';
import avatar from '../assets/default-avatar.png'

const randomImage = `https://picsum.photos/1600/370?random=${Math.floor(Math.random() * 1000)}`;

const activeBtnStyles = 'bg-red-500 text-white font-bold p-2 rounded-full w-20 outline-none';
const notActiveBtnStyles = 'bg-primary mr-4 text-black font-bold p-2 rounded-full w-20 outline-none dark:text-white';

const UserProfile = () => {
  const [user, setUser] = useState(null);
  const [pins, setPins] = useState([]);
  const [text, setText] = useState('Created');
  const [activeBtn, setActiveBtn] = useState('created');
  const [loadingUser, setLoadingUser] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const [editingName, setEditingName] = useState(false);
  const [newUserName, setNewUserName] = useState('');

  const navigate = useNavigate();
  const { userId } = useParams();

  useEffect(() => {
    async function fetchUser() {
      setLoadingUser(true);

      if (userId?.startsWith('guest_')) {
        const localUser = JSON.parse(localStorage.getItem('user'));
        if (localUser && localUser._id === userId) {
          setUser(localUser);
        } else {
          setUser({
            _id: userId,
            userName: 'Guest User',
            image: avatar,
            guest: true,
          });
        }
        setLoadingUser(false);
      } else {
        try {
          const data = await client.fetch(userQuery(userId));
          setUser(data[0] || null);
        } catch (error) {
          console.error('Error fetching user:', error);
          setUser(null);
        } finally {
          setLoadingUser(false);
        }
      }
    }

    fetchUser();
  }, [userId]);

  useEffect(() => {
    async function fetchPins() {
      if (!user) return;

      try {
        const query =
          text === 'Created' ? userCreatedPinsQuery(userId) : userSavedPinsQuery(userId);
        const data = await client.fetch(query);
        setPins(data);
      } catch (error) {
        console.error('Error fetching posts:', error);
        setPins([]);
      }
    }

    fetchPins();
  }, [text, user, userId]);

  useEffect(() => {
    function handlePinSaved() {
      if (activeBtn === 'saved') {
        setText('Saved');
      }
    }
    window.addEventListener('pin-saved', handlePinSaved);
    return () => window.removeEventListener('pin-saved', handlePinSaved);
  }, [activeBtn]);

  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);

    // Upload to Sanity (or your backend)
    client.assets
      .upload('image', file)
      .then((document) => {
        return client
          .patch(user._id)
          .set({ image: document.url })
          .commit();
      })
      .then((updatedUser) => {
        setUser(updatedUser);
        setUploading(false);
      })
      .catch(() => setUploading(false));
  };

  const handleUserNameChange = async (e) => {
    e.preventDefault();
    if (!newUserName.trim()) return;
    try {
      const updatedUser = await client
        .patch(user._id)
        .set({ userName: newUserName.trim() })
        .commit();
      setUser(updatedUser);
      setEditingName(false);
    } catch {
      // Optionally handle error
    }
  };

  const logout = () => {
    localStorage.clear();
    googleLogout();
    navigate('/login');
  };

  if (loadingUser) return <Spinner message="Loading profile..." />;

  if (!user) return <div className="flex justify-center items-center h-full text-xl">User not found</div>;

  return (
    <div className="relative pb-2 min-h-screen flex flex-col items-center justify-center bg-gradient-to-br from-blue-100 via-purple-100 to-pink-100 dark:from-gray-900 dark:via-slate-900 dark:to-blue-900 transition-colors duration-300">
      <div className="flex flex-col pb-5 w-full mx-auto">
        <div className="relative flex flex-col mb-7">
          <div className="flex flex-col justify-center items-center">
            <img
              className="w-full h-56 sm:h-72 md:h-80 lg:h-96 2xl:h-[420px] shadow-lg object-cover rounded-t-3xl"
              src={randomImage}
              alt="cover"
            />
            <div className="flex flex-col justify-center items-center">
              <div className="relative">
                <img
                  className="rounded-full w-24 h-24 -mt-12 shadow-xl object-cover border-4 border-white dark:border-blue-900"
                  src={user.image || avatar}
                  alt="user-pic"
                />
                {userId === user?._id && (
                  <>
                    <button
                      type="button"
                      className="absolute bottom-2 right-2 bg-blue-600 text-white p-1 rounded-full shadow hover:bg-blue-700 transition"
                      onClick={() => fileInputRef.current.click()}
                      disabled={uploading}
                      title="Change Profile Photo"
                    >
                      {uploading ? '...' : <FiEdit2 className="w-4 h-4" />}
                    </button>
                    <input
                      type="file"
                      accept="image/*"
                      ref={fileInputRef}
                      onChange={handleImageChange}
                      className="hidden"
                    />
                  </>
                )}
              </div>
            </div>
          </div>
          {/* Username section */}
          <div className="flex items-center justify-center mt-4">
            {editingName ? (
              <form onSubmit={handleUserNameChange} className="flex items-center gap-2">
                <input
                  type="text"
                  value={newUserName}
                  onChange={e => setNewUserName(e.target.value)}
                  className="px-3 py-1 rounded-lg border border-gray-300 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-white outline-none focus:border-blue-400"
                  autoFocus
                  maxLength={30}
                />
                <button
                  type="submit"
                  className="bg-blue-600 text-white px-3 py-1 rounded-lg font-semibold hover:bg-blue-700 transition"
                  title="Save"
                >
                  Save
                </button>
                <button
                  type="button"
                  className="bg-gray-300 dark:bg-gray-700 text-gray-800 dark:text-white px-3 py-1 rounded-lg font-semibold hover:bg-gray-400 dark:hover:bg-gray-600 transition"
                  onClick={() => setEditingName(false)}
                  title="Cancel"
                >
                  Cancel
                </button>
              </form>
            ) : (
              <>
                <h1 className="font-bold text-3xl text-center text-gray-900 dark:text-white drop-shadow">{user.userName}</h1>
                {userId === user?._id && (
                  <button
                    type="button"
                    className="ml-2 text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300 transition"
                    onClick={() => {
                      setNewUserName(user.userName);
                      setEditingName(true);
                    }}
                    title="Edit Username"
                  >
                    <FiEdit2 className="w-4 h-4" />
                  </button>
                )}
              </>
            )}
          </div>

          {userId === user?._id && (
            <div className="absolute top-4 right-4">
              <button
                type="button"
                className="bg-white dark:bg-gray-800 p-2 rounded-full cursor-pointer outline-none shadow-md hover:bg-red-100 dark:hover:bg-red-900 transition"
                onClick={logout}
                title="Logout"
              >
                <AiOutlineLogout color="red" fontSize={22} />
              </button>
            </div>
          )}
        </div>

        <div className="text-center mb-7 flex justify-center gap-4">
          <button
            type="button"
            onClick={() => {
              setText('Created');
              setActiveBtn('created');
            }}
            className={`transition-all duration-200 ${activeBtn === 'created'
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold shadow px-6 py-2 rounded-full scale-105'
              : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-white font-bold px-6 py-2 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900'
              }`}
          >
            Created
          </button>
          <button
            type="button"
            onClick={() => {
              setText('Saved');
              setActiveBtn('saved');
            }}
            className={`transition-all duration-200 ${activeBtn === 'saved'
              ? 'bg-gradient-to-r from-blue-500 to-purple-500 text-white font-bold shadow px-6 py-2 rounded-full scale-105'
              : 'bg-white dark:bg-gray-800 text-gray-800 dark:text-white font-bold px-6 py-2 rounded-full hover:bg-blue-100 dark:hover:bg-blue-900'
              }`}
          >
            Saved
          </button>
        </div>

        <div className="px-2">
          {pins?.length > 0 ? (
            <MasonryLayout pins={pins} />
          ) : (
            <div className="flex justify-center font-bold items-center w-full text-xl mt-6 text-gray-500 dark:text-gray-300">
              No Posts Found!
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default UserProfile;